import React, { Component } from "react";
import * as Cookies from "es-cookie";
import { Row, Col, Badge } from "react-bootstrap";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as compose from "lodash.flowright";
import { withRouter } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import TabSection from "./TabSection";
import { PageShimmer } from "./Shimmer";
import { pointTooltip } from "./chartTooltip";

const PREDICTIVE_QUERY = gql`
  query (
    $mobile: String!
    $exam_type: Int
    $exam_stream: Int
    $target: String
    $bucket: String
    $fromDate: String
    $toDate: String
  ) {
    getStudentPredictiveAnalytics(
      mobile: $mobile
      exam_type: $exam_type
      exam_stream: $exam_stream
      target: $target
      bucket: $bucket
      fromDate: $fromDate
      toDate: $toDate
    ) {
      rankTrend {
        period
        label
        examCount
        avgRank
        bestRank
        avgSectionRank
        bestSectionRank
        avgBranchRank
        bestBranchRank
        avgCityRank
        bestCityRank
        avgStateRank
        bestStateRank
        avgScore
        avgPercentile
      }
      prediction {
        target
        maxScore
        examsAnalyzed
        currentAvgScore
        trajectorySlope
        trend
        predictedScore
        projectedAccuracy
        platformPercentile
        projectedPercentile
        simulatedAIR
        projectedAIR
        cutoffScore
        meetsCutoff
        message
      }
        percentileStanding {
        examsConsidered
        sectionPercentile
        branchPercentile
        platformPercentile
        sectionSize
        branchSize
      }
      subjectTrend {
        subject_id
        subject
        points {
          period
          label
          avgScore
          accuracy
        }
      }
    }
  }
`;

// JEE / NEET map to the `target` arg (drives maxScore + cutoff).
const TARGETS = [
    { value: "neet", label: "NEET" },
    { value: "jee", label: "JEE" },
];

const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};

// College bridge: the viewed student comes from props (route state). Different
// entry points name the mobile field differently, so check every known variant.
// The student-app tab keys on mobile.
const resolveMobile = (student) => {
    if (!student) return null;
    return (
        student.contact_no || student.student_mobile || student.mobile ||
        student.studentMobile || student.parent_mobile || student.phone ||
        student.mobile_no || student.username || null
    );
};

// College view: this profile shows ANOTHER student, so "You/Your" becomes the
// student's (first) name. Falls back to "Student" if no name.
const studentName = (student) => {
    const full = (student && (student.student_name || student.name)) || "";
    return full.trim() ? full.trim() : "Student";
};
const studentFirstName = (student) => studentName(student).split(/\s+/)[0];

// Default the target from the VIEWED student's exam_id (2 = JEE, 1 = NEET), which
// is the authoritative signal, falling back to their exam-name text, then JEE.
// (Mirrors OverviewTab.resolveTarget — never keys off the viewer's own cookie.)
const resolveDefaultTarget = (student) => {
    const examId = num(student?.exam_id);
    if (examId === 1) return "neet";
    if (examId === 2) return "jee";
    const exam = `${student?.exam_name || student?.stream_name || student?.exam || Cookies.get("exam_name") || ""}`.toLowerCase();
    if (exam.includes("neet") || exam.includes("bipc") || exam.includes("medical")) return "neet";
    if (exam.includes("jee")) return "jee";
    return "jee";
};

const TREND_VARIANT = { improving: "success", declining: "danger", stable: "secondary" };

class PredictionRankTab extends Component {
    constructor(props) {
        super(props);
        this.state = {
            target: resolveDefaultTarget(props.student),
            examType: 0,
            bucket: "month",
            rankLevel: "section", // default to Section (student's immediate batchmates)
            prediction: null,
            rankTrend: [],
            percentileStanding: null,
            subjectTrend: [],
            rankTrendView: "graph", // "graph" | "table"
            subjectTrendView: "graph", // "graph" | "table"
            loading: false,
        };
    }

    componentDidMount() {
        this.fetch();
    }

    // Re-fetch when the header exam-stream (Mains/Advanced) changes.
    componentDidUpdate(prevProps) {
        if (prevProps.examStream !== this.props.examStream) {
            this.fetch();
        }
    }

    fetch = async () => {
        const mobile = resolveMobile(this.props.student);
        if (!mobile) {
            this.setState({ prediction: null, rankTrend: [], subjectTrend: [], loading: false });
            return;
        }
        this.setState({ loading: true });
        try {
            const res = await this.props.client.query({
                query: PREDICTIVE_QUERY,
                variables: {
                    mobile: String(mobile),
                    exam_type: Number(this.state.examType) || 0,
                    exam_stream: this.props.examStream != null ? Number(this.props.examStream) : null,
                    target: this.state.target,
                    bucket: this.state.bucket,
                    fromDate: null,
                    toDate: null,
                },
                fetchPolicy: "network-only",
            });
            const data = res?.data?.getStudentPredictiveAnalytics;
            this.setState({
                prediction: data?.prediction || null,
                rankTrend: data?.rankTrend || [],
                percentileStanding: data?.percentileStanding || null,
                subjectTrend: data?.subjectTrend || [],
                loading: false,
            });
        } catch (e) {
            console.log(e);
            this.setState({ prediction: null, rankTrend: [], percentileStanding: null, subjectTrend: [], loading: false });
        }
    };

    setTarget = (target) => {
        if (target === this.state.target) return;
        this.setState({ target }, this.fetch);
    };

    setBucket = (bucket) => {
        if (bucket === this.state.bucket) return;
        this.setState({ bucket }, this.fetch);
    };

    // Monthly / Weekly period toggle only. The exam target (JEE/NEET) is the
    // student's own program, so it's not shown as a selectable control.
    renderControls = () => (
        <div className="d-flex flex-wrap align-items-center" style={{ gap: "10px" }}>
            <div className="sp-segment">
                <button
                    type="button"
                    className={this.state.bucket === "month" ? "active" : ""}
                    onClick={() => this.setBucket("month")}
                >
                    <i className="far fa-calendar-alt" />Monthly
                </button>
                <button
                    type="button"
                    className={this.state.bucket === "week" ? "active" : ""}
                    onClick={() => this.setBucket("week")}
                >
                    <i className="far fa-calendar-week" />Weekly
                </button>
            </div>
        </div>
    );

    renderMetric = (label, value, sub, subColor) => (
        <Col md={3} sm={6} className="mb-3 mb-md-0" style={{ minWidth: 0 }}>
            <div className="text-muted mb-1" style={{ fontSize: "13px", fontWeight: 600 }}>
                {label}
            </div>
            <div className="text-dark" style={{ fontFamily: "'Outfit', sans-serif", fontSize: "2rem", lineHeight: 1.1, fontWeight: 700 }}>
                {value}
            </div>
            {sub ? (
                <div className="mt-1" style={{ fontSize: "12px", color: subColor || "#64748b" }}>
                    {sub}
                </div>
            ) : null}
        </Col>
    );

    // Score prediction summary — matches the design (4 KPIs + message line).
    renderPrediction = (prediction, targetLabel) => (
        <div
            className="p-4 mb-4"
            style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}
        >
            <h6 className="font-weight-bold text-dark mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Predicted Score &amp; All-India Rank ({targetLabel})
            </h6>
            <p className="text-muted mb-4" style={{ fontSize: "12px" }}>
                Projected {targetLabel} score and simulated All-India Rank based on {studentFirstName(this.props.student)}'s performance trajectory — a forecast, not the actual exam rank above.
            </p>
            {!prediction ? (
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                    Not enough full-length attempts yet to project a score.
                </p>
            ) : (
                <Row>
                    {this.renderMetric(
                        `Predicted ${(prediction.target || targetLabel || "").toUpperCase()} Score`,
                        <>
                            {prediction.predictedScore}
                            <small className="text-muted" style={{ fontSize: "1rem" }}>/{prediction.maxScore}</small>
                        </>,
                        <span>
                            <Badge variant={TREND_VARIANT[prediction.trend] || "secondary"} className="mr-1">
                                {prediction.trend}
                            </Badge>
                            <span className="text-muted">
                                {num(prediction.trajectorySlope) >= 0 ? "+" : ""}
                                {prediction.trajectorySlope} pts/test
                            </span>
                        </span>
                    )}
                    {this.renderMetric(
                        "Current Avg (scaled)",
                        <>
                            {prediction.currentAvgScore}
                            <small className="text-muted" style={{ fontSize: "1rem" }}>/{prediction.maxScore}</small>
                        </>,
                        `${prediction.examsAnalyzed} attempts analysed`,
                        "#4f46e5"
                    )}
                    {this.renderMetric(
                        "Simulated All-India Rank",
                        prediction.projectedAIR != null ? Number(prediction.projectedAIR).toLocaleString() : "—",
                        `projected percentile ${prediction.projectedPercentile}`,
                        "#f97316"
                    )}
                    {this.renderMetric(
                        `Target Cutoff (${prediction.cutoffScore})`,
                        <Badge
                            variant={prediction.meetsCutoff ? "success" : "danger"}
                            style={{ fontSize: "1rem", padding: "8px 14px" }}
                        >
                            {prediction.meetsCutoff ? "On Track" : "Below Cutoff"}
                        </Badge>
                    )}
                    {prediction.message ? (
                        <Col xs={12}>
                            <p className="text-muted mb-0 mt-2" style={{ fontSize: "13px" }}>
                                {prediction.message}
                            </p>
                        </Col>
                    ) : null}
                </Row>
            )}
        </div>
    );

    // ── MOU #6: Percentile Standing — within batch (section/branch), across
    // batches (platform), and the simulated All-India percentile. ─────────────
    renderPercentileStanding = (ps, prediction) => {
        if (!ps && !prediction) return null;
        const airPct = prediction && prediction.projectedPercentile != null ? num(prediction.projectedPercentile) : null;
        const cards = [
            { label: "Within Section", value: ps && ps.sectionPercentile != null ? ps.sectionPercentile : null, sub: ps && ps.sectionSize ? `of ${ps.sectionSize} in ${studentFirstName(this.props.student)}'s section` : "section", color: "#6366f1" },
            { label: "Within Branch", value: ps && ps.branchPercentile != null ? ps.branchPercentile : null, sub: ps && ps.branchSize ? `of ${ps.branchSize} in ${studentFirstName(this.props.student)}'s branch` : "branch", color: "#0ea5e9" },
            { label: "Across Batches", value: ps && ps.platformPercentile != null ? ps.platformPercentile : null, sub: "all students on the platform", color: "#a855f7" },
            { label: "All-India (simulated)", value: airPct, sub: "projected NEET/JEE percentile", color: "#f59e0b" },
        ];
        const pctColor = (v) => (v == null ? "#94a3b8" : v >= 90 ? "#16a34a" : v >= 70 ? "#0ea5e9" : v >= 40 ? "#d97706" : "#dc2626");
        return (
            <div className="p-4 mb-4" style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}>
                <h6 className="font-weight-bold text-dark mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Percentile Standing
                </h6>
                <p className="text-muted mb-4" style={{ fontSize: "12px" }}>
                    Where {studentFirstName(this.props.student)} stands within their section &amp; branch (batch), across all batches, and their simulated All-India percentile
                    {ps && ps.examsConsidered ? ` · ${ps.examsConsidered} exams considered` : ""}
                </p>
                <Row>
                    {cards.map((c) => (
                        <Col md={3} sm={6} key={c.label} className="mb-3 mb-md-0">
                            <div className="p-3 h-100" style={{ borderRadius: 14, border: "1px solid #f1f2f5", background: "#fafaff" }}>
                                <div className="text-muted mb-1" style={{ fontSize: 12.5, fontWeight: 600 }}>{c.label}</div>
                                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.9rem", fontWeight: 700, lineHeight: 1.1, color: pctColor(c.value) }}>
                                    {c.value != null ? `${c.value}` : "—"}
                                    {c.value != null ? <small className="text-muted" style={{ fontSize: "0.9rem", fontWeight: 600 }}> %ile</small> : null}
                                </div>
                                <div className="text-muted mt-1" style={{ fontSize: 11 }}>{c.sub}</div>
                            </div>
                        </Col>
                    ))}
                </Row>
            </div>
        );
    };

    // Rank Movement Over Time — avg/best rank (reversed) + percentile.
    renderRankTrend = (rankTrend) => {
        const categories = rankTrend.map((p) => p.label || "-");
        // Rank level: overall (institute), section, or branch. Each period carries
        // avg/best rank per level; pick the fields for the chosen level.
        const level = this.state.rankLevel || "overall";
        const AVG = { overall: "avgRank", section: "avgSectionRank", branch: "avgBranchRank", city: "avgCityRank", state: "avgStateRank" }[level];
        const BEST = { overall: "bestRank", section: "bestSectionRank", branch: "bestBranchRank", city: "bestCityRank", state: "bestStateRank" }[level];
        const levelLabel = { overall: "Overall", section: "Section", branch: "Branch", city: "City", state: "State" }[level];
        // Adapter so the rest of the method reads avgRank/bestRank generically.
        rankTrend = rankTrend.map((p) => ({ ...p, avgRank: p[AVG], bestRank: p[BEST] }));
        // Improvement/decline badge (MOU: "improvement or decline tracked"). Compare
        // the earliest vs latest period's avg rank. Lower rank = better, so a drop
        // in rank number is an IMPROVEMENT.
        const ranked = rankTrend.filter((p) => p.avgRank != null);
        let movement = null;
        if (ranked.length >= 2) {
            const first = num(ranked[0].avgRank);
            const last = num(ranked[ranked.length - 1].avgRank);
            const delta = first - last; // positive = improved (rank got smaller)
            movement = {
                improved: delta > 0,
                same: delta === 0,
                amount: Math.abs(Math.round(delta)),
                from: first,
                to: last,
            };
        }
        // Per-point rank data (with the previous rank attached) so each label can
        // show ▼ improved / ▲ declined vs the previous period. Lower rank = better,
        // so a DROP in rank number is an improvement (green ▼).
        const avgRankSeries = rankTrend.map((p, i) => {
            const y = p.avgRank != null ? Number(p.avgRank) : null;
            const prev = i > 0 && rankTrend[i - 1].avgRank != null ? Number(rankTrend[i - 1].avgRank) : null;
            return { y, prevRank: prev };
        });
        const options = {
            chart: { type: "line", backgroundColor: "transparent", height: 300 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: {
                categories,
                lineColor: "#e2e8f0",
                labels: { style: { color: "#64748b", fontWeight: "600" } },
            },
            yAxis: [
                { title: { text: "Rank (lower is better ↑)", style: { color: "#94a3b8", fontSize: "11px" } }, reversed: true, allowDecimals: false, gridLineColor: "#eef2f6" },
                { title: { text: "Percentile (higher is better)", style: { color: "#94a3b8", fontSize: "11px" } }, min: 0, max: 100, opposite: true, gridLineColor: "#eef2f6" },
            ],
            tooltip: pointTooltip({ unitBySeries: { "Percentile": "%" } }),
            legend: { align: "center", verticalAlign: "top" },
            series: [
                {
                    name: "Avg Rank",
                    data: avgRankSeries,
                    color: "#077EE6",
                    yAxis: 0,
                    // Per-point direction: ▼ green when rank improved (dropped),
                    // ▲ red when it worsened, vs the previous period.
                    dataLabels: {
                        enabled: true,
                        useHTML: true,
                        formatter: function () {
                            const prev = this.point.prevRank;
                            if (prev == null) return `<span style="font-weight:700;color:#334155">${this.y}</span>`;
                            const d = prev - this.y; // positive = improved (rank dropped)
                            if (d === 0) return `<span style="font-weight:700;color:#334155">${this.y}</span>`;
                            const up = d > 0; // improved
                            return `<span style="font-weight:700;color:#334155">${this.y}</span> <span style="font-size:10px;font-weight:700;color:${up ? "#16a34a" : "#dc2626"}">${up ? "▼" : "▲"}${Math.abs(Math.round(d))}</span>`;
                        },
                    },
                },
                {
                    name: "Best Rank",
                    data: rankTrend.map((p) => (p.bestRank != null ? Number(p.bestRank) : null)),
                    color: "#27AE60",
                    dashStyle: "ShortDash",
                    yAxis: 0,
                },
                {
                    name: "Percentile",
                    data: rankTrend.map((p) => (p.avgPercentile != null ? Number(p.avgPercentile) : null)),
                    color: "#F2994A",
                    yAxis: 1,
                },
            ],
        };
        const table = (
            <div className="table-responsive">
                <table className="table table-borderless mb-0">
                    <thead>
                        <tr style={{ borderBottom: "1px solid #eef2f6" }}>
                            <th className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Period</th>
                            <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Avg Rank</th>
                            <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Change</th>
                            <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Best Rank</th>
                            <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Percentile</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rankTrend.map((p, i) => {
                            // Movement vs the previous period (lower rank = better).
                            const prev = i > 0 && rankTrend[i - 1].avgRank != null ? Number(rankTrend[i - 1].avgRank) : null;
                            const cur = p.avgRank != null ? Number(p.avgRank) : null;
                            let move = null;
                            if (prev != null && cur != null && prev !== cur) {
                                const up = prev - cur > 0; // improved (rank dropped)
                                move = { up, amt: Math.abs(Math.round(prev - cur)) };
                            }
                            return (
                                <tr key={i} style={{ borderBottom: "1px solid #f6f8fb" }}>
                                    <td className="font-weight-bold text-dark" style={{ fontSize: 13 }}>{p.label || "-"}</td>
                                    <td className="text-right font-weight-bold" style={{ fontSize: 13 }}>{p.avgRank != null ? p.avgRank : "-"}</td>
                                    <td className="text-right" style={{ fontSize: 13 }}>
                                        {move ? (
                                            <span style={{ fontWeight: 700, color: move.up ? "#16a34a" : "#dc2626" }}>
                                                {move.up ? "▼" : "▲"} {move.amt}
                                            </span>
                                        ) : <span className="text-muted">—</span>}
                                    </td>
                                    <td className="text-right" style={{ fontSize: 13 }}>{p.bestRank != null ? p.bestRank : "-"}</td>
                                    <td className="text-right text-muted" style={{ fontSize: 13 }}>{p.avgPercentile != null ? p.avgPercentile : "-"}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
        // Level selector + improvement/decline badge above the chart.
        const LEVELS = [
            { key: "overall", label: "Overall" },
            { key: "section", label: "Section" },
            { key: "branch", label: "Branch" },
            { key: "city", label: "City" },
            { key: "state", label: "State" },
        ];
        const badge = (
            <div className="d-flex align-items-center flex-wrap mt-1" style={{ gap: 8 }}>
                <div className="sp-segment sp-segment-sm">
                    {LEVELS.map((l) => (
                        <button key={l.key} type="button" className={level === l.key ? "active" : ""} onClick={() => this.setState({ rankLevel: l.key })}>
                            {l.label}
                        </button>
                    ))}
                </div>
                {movement ? (
                    <span
                        className="font-weight-bold d-inline-flex align-items-center"
                        style={{
                            fontSize: 12, gap: 6, borderRadius: 999, padding: "4px 12px",
                            background: movement.same ? "#f1f5f9" : movement.improved ? "#ecfdf5" : "#fef2f2",
                            color: movement.same ? "#64748b" : movement.improved ? "#16a34a" : "#dc2626",
                        }}
                    >
                        <i className={`fas ${movement.same ? "fa-minus" : movement.improved ? "fa-arrow-trend-up" : "fa-arrow-trend-down"}`} />
                        {movement.same
                            ? `${levelLabel} rank held steady`
                            : `${levelLabel}: ${movement.improved ? "Improved" : "Declined"} ${movement.amount} rank${movement.amount === 1 ? "" : "s"}`}
                        <span style={{ color: "#94a3b8", fontWeight: 600 }}>
                            ({movement.from} → {movement.to})
                        </span>
                    </span>
                ) : null}
            </div>
        );
        return this.renderChartCard(`${studentFirstName(this.props.student)}'s Actual Rank — ${levelLabel}`, rankTrend.length > 0, options, "No ranked exams in this period.", "rankTrendView", table, badge);
    };

    // Subject-wise Marks Trend — one line per subject across periods.
    renderSubjectTrend = (subjectTrend) => {
        // Align all subjects onto a single, time-ordered category axis.
        const labelSet = [];
        subjectTrend.forEach((s) =>
            (s.points || []).forEach((p) => {
                if (!labelSet.includes(p.label)) labelSet.push(p.label);
            })
        );
        const colors = ["#077EE6", "#27AE60", "#F2994A", "#9B51E0", "#EB5757", "#2D9CDB"];
        const options = {
            chart: { type: "line", backgroundColor: "transparent", height: 300 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: {
                categories: labelSet,
                lineColor: "#e2e8f0",
                labels: { style: { color: "#64748b", fontWeight: "600" } },
            },
            yAxis: { title: { text: "Avg Subject Score" }, gridLineColor: "#eef2f6", labels: { style: { color: "#64748b" } } },
            tooltip: pointTooltip({}),
            legend: { align: "center", verticalAlign: "top" },
            series: subjectTrend.map((s, i) => {
                const byLabel = {};
                (s.points || []).forEach((p) => {
                    byLabel[p.label] = p.avgScore;
                });
                return {
                    name: s.subject,
                    color: colors[i % colors.length],
                    data: labelSet.map((l) => (byLabel[l] != null ? Number(byLabel[l]) : null)),
                };
            }),
        };
        const table = (
            <div className="table-responsive">
                {subjectTrend.map((s, si) => {
                    const points = s.points || [];
                    return (
                        <div key={s.subject_id ?? si} className={si > 0 ? "mt-3" : ""}>
                            <div className="font-weight-bold text-dark mb-1" style={{ fontSize: 13 }}>{s.subject}</div>
                            <table className="table table-borderless mb-0">
                                <thead>
                                    <tr style={{ borderBottom: "1px solid #eef2f6" }}>
                                        <th className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Period</th>
                                        <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Avg Score</th>
                                        <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Accuracy</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {points.map((p, pi) => (
                                        <tr key={pi} style={{ borderBottom: "1px solid #f6f8fb" }}>
                                            <td className="font-weight-bold text-dark" style={{ fontSize: 13 }}>{p.label || "-"}</td>
                                            <td className="text-right font-weight-bold" style={{ fontSize: 13 }}>{p.avgScore != null ? p.avgScore : "-"}</td>
                                            <td className="text-right text-muted" style={{ fontSize: 13 }}>{p.accuracy != null ? p.accuracy : "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                })}
            </div>
        );
        return this.renderChartCard("Subject-wise Marks Trend", subjectTrend.length > 0, options, "No subject data in this period.", "subjectTrendView", table);
    };

    // Shared chart card shell so both charts line up in the grid.
    // viewKey is the state field holding "graph" | "table"; tableContent is the
    // table element to render in table mode. When omitted, no toggle is shown.
    renderChartCard = (title, hasData, options, emptyText, viewKey, tableContent, badge) => {
        const view = viewKey ? this.state[viewKey] : "graph";
        return (
            <div
                className="p-4 h-100"
                style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}
            >
                <div className="d-flex align-items-start justify-content-between mb-3" style={{ gap: 10 }}>
                    <div>
                        <h6 className="font-weight-bold text-dark mb-0" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            {title}
                        </h6>
                        {badge || null}
                    </div>
                    {viewKey && hasData ? (
                        <div className="sp-segment sp-segment-sm">
                            <button type="button" className={view === "graph" ? "active" : ""} onClick={() => this.setState({ [viewKey]: "graph" })}>
                                <i className="fas fa-chart-column" />Graph
                            </button>
                            <button type="button" className={view === "table" ? "active" : ""} onClick={() => this.setState({ [viewKey]: "table" })}>
                                <i className="fas fa-table" />Table
                            </button>
                        </div>
                    ) : null}
                </div>
                {!hasData ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                        {emptyText}
                    </div>
                ) : view === "table" ? (
                    tableContent
                ) : (
                    <HighchartsReact highcharts={Highcharts} options={options} containerProps={{ style: { width: "100%" } }} />
                )}
            </div>
        );
    };

    render() {
        const { prediction, rankTrend, subjectTrend, loading, target } = this.state;
        const mobile = resolveMobile(this.props.student);
        const targetLabel = TARGETS.find((t) => t.value === target)?.label || target.toUpperCase();

        return (
            <TabSection
                icon="fa-trophy"
                title="Prediction & Rank"
                description="Predicted scores, expected rank and percentile projections."
                headerRight={this.renderControls()}
            >
                {loading ? (
                    <PageShimmer />
                ) : !mobile ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                        No mobile number available for this student.
                    </div>
                ) : (
                    <>
                        {/* MOU #5 Rank movement → #6 Percentile standing → #7 Prediction.
                            (Subject-wise Marks Trend removed — already on Marks Trend tab, Req #1.) */}
                        <div className="mb-4">
                            {this.renderRankTrend(rankTrend)}
                        </div>
                        {this.renderPercentileStanding(this.state.percentileStanding, prediction)}
                        {this.renderPrediction(prediction, targetLabel)}
                    </>
                )}
            </TabSection>
        );
    }
}

export default compose(withApollo, withRouter)(PredictionRankTab);
